/**
 * Created by Rodrigo Quesada on 16/03/17.
 */
import {Component, OnDestroy} from '@angular/core';
import {Page} from '../../../domain/model/common/search/page.class';
import {UserSearchModel} from './model/user-search.model';
import {UserSearchSectionService} from './model/user-search-section.service';
import {StatusFilters, UserSearchFilters, UserSearchQuery, UserService} from '../../../domain/model/user/user.service';
import {Option} from '../../../components/forms/controls/selector/selector.component';
import {BehaviorSubject, Subscription} from 'rxjs';
import {UserImpersonationService} from '../../../domain/model/user/user-impersonation.service';
import {User} from '../../../domain/model/user/user.model';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {AddProfileModalComponent} from './modals/add-profile-modal.component';
import {MessageService} from '../../../components/messages/message.service';
import moment = require('moment');

//region Constants
const PAGE_SIZE = 50;

const TEXT_CHANGE_DEBOUNCE_TIME_MS = 1000;

//endregion

@Component({
    template: require('./user-search-page.component.html'),
    styles: [require('./user-search-page.component.scss')]
})
export class UserSearchPageComponent implements OnDestroy {

    defaultValues = {
        text: '',
        filter: UserSearchFilters.LAST_NAME,
        page: 0,
        status: StatusFilters.IN_PROCESS,
        recredentialed: false,
    };

    dateValues = {
        from: undefined,
        to: undefined,
    };

    get pageNumber(): number { return this.defaultValues.page; }

    set pageNumber(value: number) {
        this.defaultValues.page = value;
    }

    filters = [
        new Option(UserSearchFilters.FIRST_NAME, 'First Name'),
        new Option(UserSearchFilters.LAST_NAME, 'Last Name'),
        new Option(UserSearchFilters.MOBILE_PHONE, 'Cell Phone'),
        new Option(UserSearchFilters.EMAIL, 'Email'),
        new Option(UserSearchFilters.NPI, 'NPI Number'),
        new Option(UserSearchFilters.STATE_LICENSED, 'States Licensed'),
        new Option(UserSearchFilters.SPECIALTY, 'Specialty'),
        new Option(UserSearchFilters.STATUS, 'Status')
    ];

    statuses = [
        new Option(StatusFilters.IN_PROCESS, 'In Process'),
        new Option(StatusFilters.CANCELLED, 'Cancelled'),
        new Option(StatusFilters.ON_HOLD, 'On Hold'),
        new Option(StatusFilters.COMPLETE, 'Complete'),
        new Option(StatusFilters.EXPIRED, 'Expired'),
        new Option(StatusFilters.NOT_APPROVED, 'Not Approved')
    ];

    page?: Page<UserSearchModel>;

    searching: boolean = false;

    subscriptions: Subscription;
    searchSubscription: Subscription;

    //region Observable Search Actions
    readonly textChange$ = new BehaviorSubject<string>(this.defaultValues.text);
    readonly filterChange$ = new BehaviorSubject<string>(this.defaultValues.filter);
    //endregion

    //region Modal
    private _modalRef: NgbModalRef;
    //endregion

    //region Send Set-Password Email
    sendingSetPasswordEmail: { [userId: string]: boolean } = {};
    //endregion

    constructor(private readonly _userSearchService: UserSearchSectionService,
                private readonly _userService: UserService,
                private readonly _impersonationService: UserImpersonationService,
                private readonly _modalService: NgbModal,
                private readonly _messageService: MessageService) {
        this.subscriptions = new Subscription();
        this._subscribeToFormChanges();
    }

    ngOnDestroy(): void {
        this._destroyModal();
        if (this.subscriptions) this.subscriptions.unsubscribe();
        if (this.searchSubscription) this.searchSubscription.unsubscribe();
    }

    private _subscribeToFormChanges(): void {
        this.subscriptions.add(
            this.textChange$
                .debounceTime(TEXT_CHANGE_DEBOUNCE_TIME_MS)
                .skip(1)
                .distinctUntilChanged()
                .subscribe(() => {
                    this.pageNumber = 0;
                    this.search();
                })
        );
        this.subscriptions.add(
            this.filterChange$
                .distinctUntilChanged()
                .subscribe((filter) => {
                    this.pageNumber = 0;
                    if (filter !== UserSearchFilters.STATUS) {
                        this.search();
                    } else {
                        this.page = undefined;
                    }
                })
        );
    }

    search(): void {
        if (this.searchSubscription) this.searchSubscription.unsubscribe();
        this.page = undefined;
        const {text, status, filter, page, recredentialed} = this.defaultValues;
        let from = this.dateValues.from !== undefined ? moment(this.dateValues.from).format('YYYY-MM-DD') : '';
        let to = this.dateValues.to !== undefined ? moment(this.dateValues.to).format('YYYY-MM-DD') : '';
        this.searching = true;
        this.searchSubscription = this._userSearchService.searchUsers(
            new UserSearchQuery({
                text: text || '',
                status: status || '',
                from: from || '',
                to: to || '',
                recredentialed,
                page,
                pageSize: PAGE_SIZE,
                filter: filter
            })
        ).subscribe(
            resultsPage => {
                this.page = resultsPage;
                this.searching = false;
            },
            () => {
                this.searching = false;
            }
        );
    }

    goToPrevious(event: Event) {
        event.preventDefault();
        if (this.pageNumber > 0) --this.pageNumber;
        $('.content-wrapper').scrollTop(0); // Looks completely non-modular but seems to be the only way to get back to the top
        this.search();
    }

    goToNext(event: Event) {
        event.preventDefault();
        ++this.pageNumber;
        $('.content-wrapper').scrollTop(0);
        this.search();
    }

    impersonateUser(user: User) {
        this._impersonationService.impersonateUser(user, ['personal-details']);
    }

    //region Send Set-Password Email
    sendSetPasswordEmail(user: User) {
        const userId = user.id!!;
        if (!this.sendingSetPasswordEmail[userId]) {
            this.sendingSetPasswordEmail[userId] = true;
            this._userService.sendSetPasswordEmail(userId)
                .finally(() => this.sendingSetPasswordEmail[userId] = false)
                .subscribe(() => {
                    this._messageService.success('Info', 'The set-password email has been sent.');
                });
        }
    }
    //endregion

    //region Modal
    addProfile() {
        this._modalRef = this._modalService.open(AddProfileModalComponent, {
            backdrop: 'static',
            size: 'lg',
            keyboard: false
        });

        this._modalRef.result.then(() => this.search(), _ => {});
    }

    private _destroyModal(): void {
        if (this._modalRef) {
            this._modalRef.close();
        }
    }
    //endregion
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/pages/admin/search/user-search-page.component.ts